import React, { useState, useContext, useMemo, useEffect, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { TransactionCategory, useBillingInfo } from '@shipstation/financial-services-ui-components';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { useTaxFormContext } from '../../pages/settings/payment-and-subscription/context/tax-form-context-provider';
import { noop } from 'ui-components/default-props/noop';
import { useFsuiContext } from '../fsui-context-provider';
import { useLocale } from '../../intl/hooks';
import { usePaymentAndSubscriptionMegaContext } from '../../pages/settings/payment-and-subscription/context';
import { emptyObject } from 'ui-components/default-props/empty';
import { updatePaymentMethod } from '../api';
import { getBillingInfoFromPaymentMethod, mapBillingInfoDataToBillingAddress } from '../mapping';
import { showGenericErrorNotification } from '../notifications';
import { useWallet } from '../../wallet/hooks/use-wallet';
import { useDeprecatedLocalStorage, ADD_PAYMENT_METHOD_DATA } from '../../hooks/use-local-storage';

import { messages } from '../messages';

const PaymentUpdateContext = React.createContext({
  paymentType: null,
  setPaymentType: noop,
  setFsuiSubmitFn: noop,
  fsuiSubmitFn: noop,
});

export const usePaymentUpdateContext = () => useContext(PaymentUpdateContext);

export const PaymentUpdateContextProvider = ({ children, transactionCategory, onClose, trackConfirmClick }) => {
  const locale = useLocale();
  const seller = useSelector((state) => state.seller) || {};
  const { homeCountry, companyName, phone } = seller || {};
  const { paymentMethodDetails: paymentMethods, reloadData, isBillingInfoLoading } = usePaymentAndSubscriptionMegaContext();
  const { isPreloadingSession, preloadedSession, currencyCode, onPaymentCompleted, onError } = useFsuiContext();
  const { isInvalidForm: isInvalidTaxForm, isEditing: isEditingTaxForm, submitTaxForm, isTaxSectionOpen } = useTaxFormContext();
  const { billingInfo, setBillingInfo } = useBillingInfo();
  const billingInfoRef = useRef(billingInfo);
  const { walletId } = useWallet();

  const [paymentType, setPaymentType] = useState(null);
  const [fsuiSubmitFn, setFsuiSubmitFn] = useState(noop);
  const [performFormValidation, setPerformFormValidation] = useState(noop);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    paymentMethods?.find((method) => method.assignedPreferences?.includes(transactionCategory)) || null,
  );
  const [isAddingNew, setIsAddingNew] = useState(paymentMethods?.length === 0);
  const [, setDataToStorage] = useDeprecatedLocalStorage(ADD_PAYMENT_METHOD_DATA, emptyObject);

  const setBillingInfoForMethod = useCallback(
    (method) => {
      if (!method) {
        setBillingInfo(
          {
            countryCode: homeCountry,
          },
          false,
        );
        return;
      }
      setBillingInfo(
        getBillingInfoFromPaymentMethod(method, {
          homeCountry,
          phone,
          companyName,
        }),
        true,
      );
    },
    [setBillingInfo, homeCountry, companyName, phone],
  );

  const handleSubmitTaxForm = useCallback(async () => {
    if (isEditingTaxForm && isTaxSectionOpen) {
      try {
        await submitTaxForm();
      } catch (error) {
        NotificationService.showNotification({
          type: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.taxUpdateErrorTitle} />,
          content: <FormattedMessage {...messages.taxUpdateErrorMessage} />,
        });
        throw error; // rethrow to handle in the calling function
      }
    }
  }, [isEditingTaxForm, isTaxSectionOpen, submitTaxForm]);

  const handleSetFsuiSubmitPaymentFunction = useCallback((submit) => {
    setFsuiSubmitFn(() => submit);
  }, []);
  const handleSetPerformFormValidation = useCallback((validationFn) => {
    setPerformFormValidation(() => validationFn);
  }, []);

  const handleClickOnSwitchMode = useCallback(() => {
    setBillingInfoForMethod(
      isAddingNew
        ? selectedPaymentMethod
        : {
            countryCode: homeCountry,
          },
    );
    setIsAddingNew((prev) => !prev);
  }, [isAddingNew, setBillingInfoForMethod, selectedPaymentMethod, homeCountry]);

  const handlePaymentMethodChange = useCallback(
    (method) => {
      setSelectedPaymentMethod(method);
      setBillingInfoForMethod(method);
    },
    [setBillingInfoForMethod],
  );

  const handleBeforeRedirect = useCallback(async () => {
    trackConfirmClick?.();
    const payload = {
      assignedPreferences: [transactionCategory],
      currencyCode,
      sessionId: preloadedSession?.auctanePaySessionId,
    };
    if (billingInfoRef?.current) {
      payload.infoUpdateRequest = {
        billingAddress: mapBillingInfoDataToBillingAddress(billingInfoRef?.current),
      };
    }
    if (transactionCategory === TransactionCategory.AddFunds) {
      payload.walletUpdateRequest = { walletId };
    }

    setDataToStorage({ updatePayload: payload });

    // do not catch so that redirect is blocked on error
    await handleSubmitTaxForm();
  }, [
    currencyCode,
    handleSubmitTaxForm,
    preloadedSession?.auctanePaySessionId,
    setDataToStorage,
    trackConfirmClick,
    transactionCategory,
    walletId,
  ]);

  const handleError = useCallback(
    (_step, _paymentMethod, error) => {
      onError(error);
    },
    [onError],
  );

  const handleSuccess = useCallback(() => {
    trackConfirmClick?.();

    const payload = {
      assignedPreferences: [transactionCategory],
      currencyCode,
    };
    if (billingInfoRef?.current) {
      payload.infoUpdateRequest = {
        billingAddress: mapBillingInfoDataToBillingAddress(billingInfoRef?.current),
      };
    }
    if (isAddingNew) {
      payload.sessionId = preloadedSession?.auctanePaySessionId;
    } else {
      payload.paymentMethodId = selectedPaymentMethod?.id;
    }
    if (transactionCategory === TransactionCategory.AddFunds) {
      payload.walletUpdateRequest = { walletId };
    }

    Promise.all([
      updatePaymentMethod(payload)
        .then((result) => {
          if (!result.success) {
            throw new Error(result.errorMessage);
          }
        })
        .catch(showGenericErrorNotification),
      handleSubmitTaxForm(),
    ]).then(() => {
      reloadData();
      onClose();
    });
  }, [
    currencyCode,
    transactionCategory,
    isAddingNew,
    handleSubmitTaxForm,
    trackConfirmClick,
    reloadData,
    onClose,
    preloadedSession?.auctanePaySessionId,
    selectedPaymentMethod?.id,
    walletId,
  ]);

  const handlePaymentCompleted = useCallback(
    (result) => {
      onPaymentCompleted(result, handleSuccess, handleError);
    },
    [onPaymentCompleted, handleSuccess, handleError],
  );

  const creditCardConfig = useMemo(
    () => ({
      currencyCode,
      inputBorderRadius: '4px',
      inputHeight: '32px',
      locale,
      onError: handleError,
      onPaymentCompleted: handlePaymentCompleted,
      returnUrl: window.location.origin,
      submitPayment: handleSetFsuiSubmitPaymentFunction,
      transactionCategory,
    }),
    [currencyCode, locale, handleError, handlePaymentCompleted, handleSetFsuiSubmitPaymentFunction, transactionCategory],
  );

  const payByBankUsConfig = useMemo(
    () => ({
      beforeRedirect: handleBeforeRedirect,
      currencyCode,
      locale,
      session: preloadedSession,
      onError: handleError,
      onPaymentCompleted: noop, // unused in PBB directly because of redirect
      returnUrl: window.location.href,
      submitPayment: handleSetFsuiSubmitPaymentFunction,
      transactionCategory,
    }),
    [handleBeforeRedirect, currencyCode, locale, preloadedSession, handleError, handleSetFsuiSubmitPaymentFunction, transactionCategory],
  );

  const onAction = useCallback(async () => {
    performFormValidation?.();

    if (isTaxSectionOpen && isInvalidTaxForm()) {
      return;
    }

    if (!isAddingNew) {
      handleSuccess();
      return;
    }

    fsuiSubmitFn?.();
  }, [fsuiSubmitFn, handleSuccess, isAddingNew, isInvalidTaxForm, isTaxSectionOpen, performFormValidation]);

  useEffect(() => {
    if (preloadedSession && paymentType === null) {
      setPaymentType(preloadedSession?.eligiblePaymentMethodTypes?.[0] || null);
    }
  }, [paymentType, preloadedSession]);

  useEffect(() => {
    billingInfoRef.current = billingInfo;
  }, [billingInfo]);

  const value = useMemo(
    () => ({
      paymentType,
      setPaymentType,
      setFsuiSubmitFn,
      onAction,
      selectedPaymentMethod,
      setSelectedPaymentMethod,
      isAddingNew,
      isPreloadingSession,
      isBillingInfoLoading,
      paymentMethods,
      handlePaymentMethodChange,
      handleClickOnSwitchMode,
      currencyCode,
      creditCardConfig,
      payByBankUsConfig,
      preloadedSession,
      handleSetPerformFormValidation,
      billingInfo,
    }),
    [
      paymentType,
      onAction,
      selectedPaymentMethod,
      isAddingNew,
      isPreloadingSession,
      isBillingInfoLoading,
      paymentMethods,
      handlePaymentMethodChange,
      handleClickOnSwitchMode,
      currencyCode,
      creditCardConfig,
      payByBankUsConfig,
      preloadedSession,
      handleSetPerformFormValidation,
      billingInfo,
    ],
  );

  return <PaymentUpdateContext.Provider value={value}>{children}</PaymentUpdateContext.Provider>;
};
